import React, { useState, useRef, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import {
  Settings,
  ArrowLeft,
  Globe,
  DollarSign,
  MoreVertical,
  LogOut,
  Home,
  Layout,
  BookOpen,
} from 'lucide-react';

interface AdminDashboardHeaderProps {
  onLogout: () => void;
}

const navLinkClass = 'flex items-center space-x-1 sm:space-x-2 text-sm sm:text-base px-3 sm:px-4';

const AdminNavLink: React.FC<{
  to: string;
  label: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
}> = ({ to, label, icon: Icon }) => (
  <NavLink
    to={to}
    className={({ isActive }) => `${isActive ? 'btn-primary' : 'btn-secondary'} ${navLinkClass}`}
  >
    <Icon size={16} className="sm:w-[18px] sm:h-[18px]" />
    <span>{label}</span>
  </NavLink>
);

export const AdminDashboardHeader: React.FC<AdminDashboardHeaderProps> = ({ onLogout }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="page-header sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-4 sm:py-6 gap-4">
          {/* Left side: Back arrow, icon, and title */}
          <div className="animate-fade-in">
            <div className="flex items-center space-x-3">
              <Link to="/dashboard" className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
                <ArrowLeft size={20} className="text-gray-600" />
              </Link>
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-r from-purple-600 to-purple-700 flex items-center justify-center">
                <Settings size={20} className="text-white sm:w-6 sm:h-6" />
              </div>
              <div>
                <h1 className="text-lg sm:text-2xl font-bold text-gray-900">
                  <Link to="/admin" className="hover:text-gray-700">
                    Admin Dashboard
                  </Link>
                </h1>
                <p className="text-gray-600 text-sm hidden sm:block">
                  Manage users and system settings
                </p>
              </div>
            </div>
          </div>

          {/* Right side: Action buttons */}
          <div className="flex items-center justify-between sm:justify-end space-x-2 sm:space-x-3">
            {/* Primary nav - always visible */}
            <AdminNavLink to="/admin/howto" label="How To" icon={BookOpen} />
            <AdminNavLink to="/admin/templates" label="Templates" icon={Layout} />
            <AdminNavLink to="/admin/sites" label="Sites" icon={Globe} />
            <AdminNavLink to="/admin/financials" label="Financials" icon={DollarSign} />

            {/* Secondary actions - hidden on mobile, in dropdown */}
            <Link to="/dashboard" className="hidden sm:inline-flex btn-ghost">
              Back to Dashboard
            </Link>
            <button onClick={onLogout} className="hidden sm:inline-flex btn-ghost">
              Sign out
            </button>

            {/* More menu - visible only on mobile */}
            <div className="relative sm:hidden" ref={menuRef}>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                aria-expanded={isMenuOpen}
                aria-haspopup="true"
              >
                <MoreVertical size={20} />
              </button>

              {isMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  <Link
                    to="/dashboard"
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Home size={16} className="mr-3 text-gray-400" />
                    Back to Dashboard
                  </Link>
                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      onLogout();
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <LogOut size={16} className="mr-3 text-gray-400" />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};
